import { useState, useEffect, useCallback, useContext } from "react"
import classes from "./Management.module.scss";
import { getUsers, createUserPurchase, getUserPurchases, getAllPurchases, updateUserPurchase, updateUserPurchaseStatus } from "../../services"
import AuthContext from "../../store/auth-context"

// Mirrors ALLOWED_STATUS_TRANSITIONS in
// Backend/app/controllers/purchase.controller.js. POST /purchase/status accepts a
// move only when it is listed here and answers 409 for anything else, so the select
// must never offer a status the server would refuse.
const purchaseStatusTransitions = {
  "Payment Due": ["Transporting to Storage"],
  "Transporting to Storage": ["Booking Shipment"],
  "Booking Shipment": ["Shipment Confirmed"],
  "Shipment Confirmed": ["In Transit"],
  "In Transit": ["Arrived at Destination Port"],
  "Arrived at Destination Port": ["Ready for Pickup"],
  "Ready for Pickup": []
}

// Every purchase opens here; the server picks the starting state itself
const initialPurchaseStatus = "Payment Due"

// Same fallback as the server's getAllowedTransitions(): legacy rows saved before the
// status enum existed carry no status at all, and the only move open to them is onto
// the start of the workflow.
const getAllowedTransitions = (currentStatus) => {
  if (Object.prototype.hasOwnProperty.call(purchaseStatusTransitions, currentStatus)) {
    return purchaseStatusTransitions[currentStatus]
  }
  return [initialPurchaseStatus]
}

const emptyShippingDetail = {
  shipName: "",
  pickupPort: "",
  deliveryPort: "",
  estimatedPickupDate: "",
  estimatedArrivalDate: "",
  imo: "",
}

const PurchaseManagement = () => {
  const authCtx = useContext(AuthContext);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [purchaseForm, setPurchaseForm] = useState({
    carId: "",
    payment: "",
    vin: "",
    purchaseDate: "",
    invoice: "",
  });
  const [inputValue, setInputValue] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const [shippingDetail, setShippingDetail] = useState({ ...emptyShippingDetail });
  const [statusUpdate, setStatusUpdate] = useState("");

  // Build a YYYY-MM-DD string from the admin's LOCAL date (avoids UTC off-by-one)
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Add a new purchase
  const handleAddPurchase = () => {
    if (!selectedUserId || !purchaseForm.carId || !purchaseForm.payment) {
      alert("Please fill out all required fields.");
      return;
    }

    // POST /purchase validates `payment` as a positive number, and runs `invoice`
    // through isSafeInvoice() (Backend/app/controllers/purchase.controller.js:54-65):
    // an empty value, a plain invoice NUMBER, a stored path and an https link are all
    // accepted; only another explicit scheme (javascript:, data:, vbscript:, ...) or a
    // protocol-relative //host reference is refused with a 400. Mirror exactly that
    // rule here so the admin is told in plain language before the request goes out —
    // a stricter client check would block input the server would happily store.
    const paymentAmount = Number(String(purchaseForm.payment).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      alert("Payment must be a positive number.");
      return;
    }

    const invoice = purchaseForm.invoice.trim();
    // Same collapse the server performs: browsers ignore whitespace and control
    // characters when resolving a scheme, so "java\nscript:..." must be tested without
    // them. A single leading letter is a Windows drive path, not a scheme, hence 2+.
    const collapsedInvoice = Array.from(invoice)
      .filter((ch) => ch.charCodeAt(0) > 32 && ch.charCodeAt(0) !== 127 && !/\s/.test(ch))
      .join("");
    const invoiceScheme = collapsedInvoice.match(/^([a-z][a-z0-9+.-]+):/i);
    // "/\host" counts as protocol-relative too - the URL parser treats it like "//".
    if (
      /^[/\\]{2}/.test(collapsedInvoice) ||
      (invoiceScheme && invoiceScheme[1].toLowerCase() !== "https")
    ) {
      alert("Invoice must be an https link or a plain invoice reference - other link types are not accepted.");
      return;
    }
    // Matches the Joi `.max(2048)` on the same field.
    if (invoice.length > 2048) {
      alert("Invoice must be 2048 characters or shorter.");
      return;
    }

    const newPurchase = {
      id: purchases.length + 1,
      userId: selectedUserId,
      ...purchaseForm,
      payment: paymentAmount,
      invoice,
      status: initialPurchaseStatus,
      shippingDetail: null,
      transportDetail: {
        [initialPurchaseStatus]: getLocalDateString(),
      },
    };

    createUserPurchase(newPurchase).then((res) => {
      if (res?.message === "Purchase created successfully") {
        console.log("Purchase created successfully");
        // Use the row the server actually stored (it derives status, payment and the
        // purchase date itself, and only its copy carries the real _id).
        setPurchases([...purchases, res?.data || newPurchase]);
        setPurchaseForm({
          carId: "",
          payment: "",
          vin: "",
          purchaseDate: "",
          invoice: "",
        });
      } else {
        console.log("Purchase creation failed");
        alert(res?.message || "Failed to create purchase. Please try again.");
      }
    });
  };

  // Update purchase status
  const updatePurchaseStatus = (id) => {
    let purchase = purchases.find((purchase) => purchase._id === id || purchase.id === id);
    // Only ever send a move the server's state machine accepts; anything else is a 409.
    if(getAllowedTransitions(purchase?.status).indexOf(statusUpdate) === -1){
      console.log("Invalid status transition");
      return;
    }
    let statusData = {
      status: statusUpdate,
      transportDetail: {
        ...purchase?.transportDetail,
        [statusUpdate]: getLocalDateString(),
      },
    };

    // The server rejects illegal transitions with a 409, so the table is only
    // updated once the write has actually succeeded. Updating it first showed the
    // admin a status the database never took.
    updateUserPurchaseStatus(statusData, id, selectedUserId).then((res) => {
      if (res?.message === "Purchase status updated successfully") {
        console.log("Purchase status updated successfully");
        setPurchases((currentPurchases) =>
          currentPurchases.map((purchase) =>
            purchase._id === id || purchase.id === id
              ? {
                  ...purchase,
                  ...statusData
                }
              : purchase
          )
        );
      } else {
        console.log("Purchase status update failed");
        alert(res?.message || "Failed to update purchase status. Please try again.");
        reloadPurchases();
      }
      setStatusUpdate("");
      setSelectedPurchaseId(null);
    }).catch((error) => {
      console.error("Purchase status update failed:", error);
      alert("Failed to update purchase status. Please try again.");
      setStatusUpdate("");
      setSelectedPurchaseId(null);
    });
  };

  // Add shipping details to a purchase
  const addShippingDetails = () => {
    setPurchases(
      purchases.map((purchase) =>
        purchase._id === selectedPurchaseId || purchase.id === selectedPurchaseId
          ? { ...purchase, shippingDetail }
          : purchase
      )
    );
    updateUserPurchase(shippingDetail,selectedPurchaseId,selectedUserId).then((res) => {
      if (res?.message === "Purchase updated successfully") {
        console.log("Purchase updated successfully");
      } else {
        console.log("Purchase update failed");
      }
    });
    setShippingDetail({ ...emptyShippingDetail });
    setSelectedPurchaseId(null);
  };

  const shippingCloseHandler = () => {
    setSelectedPurchaseId(null);
    setShippingDetail({ ...emptyShippingDetail });
  };

  const getUsersData = useCallback(() => {
    setLoading(true)
    const payload = ``
    getUsers(payload).then((res) => {
      setLoading(false)
      if (res?.message === "Success") {
        if (res?.data.length > 0) {
          setUsers(res?.data)
        } else {
          setUsers([])
        }
      } else {
        setUsers([])
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (authCtx.isLoggedIn) {
      getUsersData()
    }
  }, [getUsersData, authCtx.isLoggedIn])

  // eslint-disable-next-line no-unused-vars
  const userChangeHandler = (e) => {
    setSelectedUserId(e.target.value)
  }

  const fetchAllPurchases = useCallback(() => {
    getAllPurchases().then((res) => {
      if (res?.message === "All Purchases Found") {
        if (res?.data.length > 0) {
          setPurchases(res?.data)
        } else {
          setPurchases([])
        }
      } else {
        setPurchases([])
      }
    })
  }, [])

  useEffect(() => {
    fetchAllPurchases()
  }, [fetchAllPurchases])

  const fetchPurchases = useCallback((userId) => {
    setLoading(true)
    getUserPurchases(userId).then((res) => {
      console.log("Purchases: ", res.data)
      setLoading(false)
      if (res?.message === "Purchases Found") {
        if (res?.data.length > 0) {
          setPurchases(res?.data)
        } else {
          setPurchases([])
        }
      } else {
        setPurchases([])
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pull the authoritative list back from the server after a rejected write, so the
  // table never keeps showing a change the database refused.
  const reloadPurchases = useCallback(() => {
    if (selectedUserId) {
      fetchPurchases(selectedUserId)
    } else {
      fetchAllPurchases()
    }
  }, [selectedUserId, fetchPurchases, fetchAllPurchases])

  useEffect(() => {
    if (selectedUserId) {
      fetchPurchases(selectedUserId)
    }
  }, [selectedUserId, fetchPurchases, statusUpdate])

  const onClickManageHandler = (purchase) => {
    setSelectedPurchaseId(purchase._id || purchase.id);
    // Legacy rows can carry no status at all; the select always carries the current
    // status as a disabled option, so "" stays a valid controlled value.
    setStatusUpdate(purchase.status || "");
    if(purchase.shippingDetail) {
      // Copy only the fields PUT /purchase accepts, defaulted to "". The stored object
      // can be missing keys (uncontrolled inputs) or carry extra ones, and that
      // endpoint's Joi schema rejects any unknown key with a 400.
      setShippingDetail(
        Object.keys(emptyShippingDetail).reduce((detail, key) => {
          detail[key] = purchase.shippingDetail[key] == null ? "" : String(purchase.shippingDetail[key]);
          return detail;
        }, {})
      );
    } else {
      setShippingDetail({ ...emptyShippingDetail });
    }
  };

   // Filter users when input value changes
   useEffect(() => {
    const filtered = users.filter((user) =>
      user.firstName.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [inputValue, users]);

  const handleFocus = () => {
    setShowDropdown(true);
  }

  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 100);
  }

  // Handle selecting a user
   const handleSelect = (user) => {
    setSelectedUserId(user.id || user._id);
    setInputValue(`${user.firstName} ${user.lastName} - ${user.email || ''}`);
    setShowDropdown(false);
  }

  // Add function to check if all shipping details are filled
  const areShippingDetailsFilled = () => {
    return Object.values(shippingDetail).every(value => String(value ?? '').trim() !== '');
  };

  // Modify the status select handler
  const handleStatusChange = (e) => {
    setStatusUpdate(e.target.value);
  };

  const renderMobileCards = () => (
    <div className={classes.mobileCards}>
      {purchases.map((purchase) => (
        <div key={purchase.id || purchase._id} className={classes.mobileCard}>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Car ID</span>
            <span className={classes.cardValue}>{purchase.carId && purchase.carId._id ? purchase.carId._id : 'N/A'}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Car Name</span>
            <span className={classes.cardValue}>{purchase.carId ? getCarName(purchase.carId) : 'N/A'}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Bid No</span>
            <span className={classes.cardValue}>{purchase.carId && purchase.carId.bidNo ? purchase.carId.bidNo : 'N/A'}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Customer</span>
            <span className={classes.cardValue}>
              {purchase.userId ? `${purchase.userId.firstName || ''} ${purchase.userId.lastName || ''} - ${purchase.userId.email || 'No email'}` : 'N/A'}
            </span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Payment</span>
            <span className={classes.cardValue}>${purchase.payment || 'N/A'}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>VIN</span>
            <span className={classes.cardValue}>{purchase.vin || 'N/A'}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Status</span>
            <span className={`${classes.status} ${purchase.status ? classes[purchase.status.toLowerCase().replace(/\s+/g, '')] : ''}`}>
              {purchase.status || 'N/A'}
            </span>
          </div>
          <div className={classes.cardActions}>
            <button
              className={classes.tableButton}
              onClick={() => onClickManageHandler(purchase)}
            >
              Manage
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const selectedPurchase = purchases.find(
    (purchase) => purchase._id === selectedPurchaseId || purchase.id === selectedPurchaseId
  );

  // The select always carries the current status as a disabled option, so its value is
  // never out of range - not even for a legacy row whose status is unset ("") or is a
  // value the workflow no longer knows. The selectable options are exactly the moves
  // the server would accept; everything else is a 409.
  const currentStatusValue = selectedPurchase?.status || "";
  const nextStatuses = getAllowedTransitions(selectedPurchase?.status)
    .filter((status) => status !== currentStatusValue);

  // Derived rather than stored, so a legacy row is "changed" as soon as the admin picks
  // its only legal target instead of leaving the button stuck on disabled.
  const hasStatusChanged = statusUpdate !== "" && statusUpdate !== currentStatusValue;

  return (
    <div className="container">
      <h1 className={classes.h1}>Purchase Management</h1>

      {/* Form for Adding Purchase */}
      <div>
        <h2 className={classes.headingH2}>Add Purchase</h2>
        <form
          className={classes.purchaseForm}
          onSubmit={(e) => {
            e.preventDefault();
            handleAddPurchase();
          }}
        >
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Select User</label>
            <div className={classes.wfull}>
            <input
              type="text"
              placeholder="Search user..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={classes.formInput}
            />
            {showDropdown && inputValue && (
              <ul
                className={classes.usersList}
                style={{ listStyleType: "none", padding: 0, margin: 0, maxHeight: "150px", overflowY: "auto" }}
              >
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <li
                      key={user.id}
                      onMouseDown={() => handleSelect(user)}
                      className={classes.nameList}
                    >
                      {user.firstName} {user.lastName} - {user.email || 'No email'}
                    </li>
                  ))
                ) : (
                  <li className={classes.nameList}>No user found!</li>
                )}
              </ul>
            )}
          </div>
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Car ID</label>
            <input
              type="text"
              className={classes.formInput}
              value={purchaseForm.carId}
              onChange={(e) =>
                setPurchaseForm({ ...purchaseForm, carId: e.target.value })
              }
            />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Payment</label>
            <input
              type="number"
              className={classes.formInput}
              value={purchaseForm.payment}
              onChange={(e) =>
                setPurchaseForm({ ...purchaseForm, payment: e.target.value })
              }
            />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>VIN</label>
            <input
              type="text"
              className={classes.formInput}
              value={purchaseForm.vin}
              onChange={(e) =>
                setPurchaseForm({ ...purchaseForm, vin: e.target.value })
              }
            />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Purchase Date</label>
            <input
              type="date"
              className={classes.formInput}
              value={purchaseForm.purchaseDate}
              onChange={(e) =>
                setPurchaseForm({ ...purchaseForm, purchaseDate: e.target.value })
              }
            />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Invoice (number or https link)</label>
            <input
              type="text"
              className={classes.formInput}
              value={purchaseForm.invoice}
              onChange={(e) =>
                setPurchaseForm({ ...purchaseForm, invoice: e.target.value })
              }
            />
          </div>
          <div className={`${classes.formButtons} ${classes.button}`}>
            <button className={classes.formButtonPrimary} type="submit">
              Add Purchase
            </button>
          </div>
        </form>
      </div>

      {/* Display All Purchases */}
      {purchases && purchases.length > 0 && (
        <div>
          <h2 className={classes.headingH2}>All Purchases</h2>
          {/* Desktop View */}
          <div className={classes.desktopView}>
            <table className={classes.table}>
              <thead>
                <tr className={classes.tableHeaderRow}>
                  <th className={classes.tableHead}>Car ID</th>
                  <th className={classes.tableHead}>Car name</th> 
                  <th className={classes.tableHead}>Bid no</th>
                  <th className={classes.tableHead}>Customer Name & Email</th> 
                  <th className={classes.tableHead}>Payment</th>
                  <th className={classes.tableHead}>VIN</th>
                  <th className={classes.tableHead}>Status</th>
                  <th className={classes.tableHead}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => (
                  <tr key={purchase.id || purchase._id} className={classes.tableRow}>
                    <td className={classes.tableData}>{purchase.carId && purchase.carId._id ? purchase.carId._id : 'N/A'}</td>
                    <td className={classes.tableData}>{purchase.carId ? getCarName(purchase.carId) : 'N/A'}</td>
                    <td className={classes.tableData}>{purchase.carId && purchase.carId.bidNo ? purchase.carId.bidNo : 'N/A'}</td>
                    <td className={classes.tableData}>
                      {purchase.userId ? `${purchase.userId.firstName || ''} ${purchase.userId.lastName || ''} - ${purchase.userId.email || 'No email'}` : 'N/A'}
                    </td>
                    <td className={classes.tableData}>{purchase.payment || 'N/A'}</td>
                    <td className={classes.tableData}>{purchase.vin || 'N/A'}</td>
                    <td className={classes.tableData}>{purchase.status || 'N/A'}</td>
                    <td className={classes.tableData}>
                      <button
                        className={classes.tableButton}
                        onClick={() => onClickManageHandler(purchase)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View - Cards */}
          <div className={classes.mobileView}>
            {renderMobileCards()}
          </div>
        </div>
      )}
      {/* Manage Selected Purchase */}
      {selectedPurchaseId && (
        <div className={classes.modal}>
          <div className={classes.modalContent}>
          <h2 className={classes.headingH2}>Manage Purchase</h2>
          <hr />
          <div className={classes.modalForm}>
            <h3 className={classes.headingH3}>Update Status</h3>
            <div className={classes.inlineForm}>
            <div className={classes.formGroup}>
            <select
              className={`${classes.formInput} ${classes.formSelect}`}
              onChange={handleStatusChange}
              value={statusUpdate}
            >
              {/* Current status: shown so the controlled value always matches an
                  option, but never selectable - it is not a legal transition. */}
              <option
                className={classes.selectOption}
                value={currentStatusValue}
                disabled
              >
                {currentStatusValue || "No status set"}
              </option>
              {nextStatuses.map((status) => (
                <option
                  key={status}
                  className={classes.selectOption}
                  value={status}
                >
                  {status}
                </option>
              ))}
            </select>
            </div>
            <button
              className={classes.formButtonPrimary}
              onClick={() => updatePurchaseStatus(selectedPurchaseId)}
              disabled={!hasStatusChanged}
              style={{ opacity: !hasStatusChanged ? 0.5 : 1 }}
            >
              Update Status
            </button>
            </div>
          </div>

          <div className="form-container space-y-4 w-[500px] pt-4">
            <h3 className={classes.headingH3}>Add Shipping Details</h3>
            <hr />
            <form className={classes.modalForm}>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Ship Name</label>
              <input
                type="text"
                className={classes.formInput}
                value={shippingDetail.shipName}
                onChange={(e) =>
                  setShippingDetail({ ...shippingDetail, shipName: e.target.value })
                }
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Imo Number</label>
              <input
                type="number"
                className={classes.formInput}
                value={shippingDetail.imo}
                onChange={(e) =>
                  setShippingDetail({ ...shippingDetail, imo: e.target.value })
                }
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Pickup Port</label>
              <input
                type="text"
                className={classes.formInput}
                value={shippingDetail.pickupPort}
                onChange={(e) =>
                  setShippingDetail({ ...shippingDetail, pickupPort: e.target.value })
                }
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Delivery Port</label>
              <input
                type="text"
                className={classes.formInput}
                value={shippingDetail.deliveryPort}
                onChange={(e) =>
                  setShippingDetail({
                    ...shippingDetail,
                    deliveryPort: e.target.value,
                  })
                }
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Estimated Pickup Date</label>
              <input
                type="date"
                className={classes.formInput}
                value={shippingDetail.estimatedPickupDate}
                onChange={(e) =>
                  setShippingDetail({
                    ...shippingDetail,
                    estimatedPickupDate: e.target.value,
                  })
                }
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Estimated Arrival Date</label>
              <input
                type="date"
                className={classes.formInput}
                value={shippingDetail.estimatedArrivalDate}
                onChange={(e) =>
                  setShippingDetail({
                    ...shippingDetail,
                    estimatedArrivalDate: e.target.value,
                  })
                }
              />
            </div>
            <div className={classes.formButtons}>
              <button 
                className={classes.formButtonPrimary} 
                onClick={addShippingDetails}
                disabled={!areShippingDetailsFilled()}
                style={{ opacity: !areShippingDetailsFilled() ? 0.5 : 1 }}
                type="button"
              >
                Save Shipping Details
              </button>
              <button
                className={classes.formButtonSecondary}
                onClick={shippingCloseHandler}
                type="button"
              >
                Cancel
              </button>
            </div>
            </form>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getCarName = (content) => {
  if (!content) {
      return 'N/A';
  }
  
  // If makerDetail exists and has the required fields, use the full format
  if (content.makerDetail?.maker && content.makerDetail?.model && content.model) {
      let vehicleName = content.model + " " + content.makerDetail.maker + " " + content.makerDetail.model + " " + (content.makerDetail.type || '');
      return vehicleName;
  }
  
  // Fallback for cars from oldcars collection: use carDetail.vehicleDetailName
  if (content.carDetail?.vehicleDetailName && Array.isArray(content.carDetail.vehicleDetailName) && content.carDetail.vehicleDetailName.length > 0) {
      const [maker, model, grade] = content.carDetail.vehicleDetailName;
      let vehicleName = content.model || '';
      
      if (maker) {
          vehicleName += (vehicleName ? ' ' : '') + maker;
      }
      if (model) {
          vehicleName += (vehicleName ? ' ' : '') + model;
      }
      if (grade) {
          vehicleName += (vehicleName ? ' ' : '') + grade;
      }
      
      // Add type from vehicleDetails if available
      if (content.carDetail?.vehicleDetails) {
          const typeDetail = content.carDetail.vehicleDetails.find(detail => detail.key === 'Type');
          if (typeDetail?.value) {
              vehicleName += (vehicleName ? ' ' : '') + typeDetail.value;
          }
      }
      
      return vehicleName || 'Unknown Car';
  }
  
  // Fallback for cars that might have partial makerDetail
  let fallbackName = '';
  
  if (content.model) {
      fallbackName += content.model;
  }
  
  if (content.makerDetail?.maker) {
      fallbackName += (fallbackName ? ' ' : '') + content.makerDetail.maker;
  }
  
  if (content.makerDetail?.model) {
      fallbackName += (fallbackName ? ' ' : '') + content.makerDetail.model;
  }
  
  if (content.makerDetail?.type) {
      fallbackName += (fallbackName ? ' ' : '') + content.makerDetail.type;
  }
  
  // If we have a bid number but no other info, show that
  if (!fallbackName && content.bidNo) {
      fallbackName = 'Car #' + content.bidNo;
  }
  
  // If we have a car ID but nothing else, show that
  if (!fallbackName && content._id) {
      fallbackName = 'Car ID: ' + content._id;
  }
  
  return fallbackName || 'Unknown Car';
};

export default PurchaseManagement;
